import { AccountResourceService, KeyAndPasswordVM } from '@/openapi';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PasswordResetFinish.module.css';
import { useRemotePost } from '@/hooks/useRemotePost';
import Button from '@/components/UI/Button';
import InputField from '@/components/UI/InputField';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Logo from '@/components/UI/Logo';
import Loader from '@/components/UI/Loader';
import SuccessToast from '@/components/UI/SuccessToast';
import FadeInSection from '@/components/UI/FadeInSection';
import { useViewportHeight } from '@/hooks/useViewportHeight';

const PasswordResetFinish = () => {
	const { t } = useTranslation();
	useViewportHeight();

	const navigate = useNavigate();

	const [searchParams] = useSearchParams();
	const key = searchParams.get('key');

	const [resetPwdError, setResetPwdError] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const [pwd1, setPwd1] = useState('');
	const [pwd2, setPwd2] = useState('');

	const pwd1InputRef = useRef<HTMLInputElement>(null);
	const pwd2InputRef = useRef<HTMLInputElement>(null);

	const resetPwd = useRemotePost<void, { requestBody: KeyAndPasswordVM }>({
		remoteMethod: async (payload) => {
			return AccountResourceService.finishPasswordReset(payload);
		}
	});

	useEffect(() => {
		if (typeof resetPwd.data !== 'undefined') {
			navigate('/login');
		}
	}, [resetPwd.data]);

	useEffect(() => {
		if (typeof resetPwd.error !== 'undefined' && resetPwd.errorCode) {
			let message = '';
			if (resetPwd.errorCode === 400) {
				message = t('pwdReset.errLength');
			} else if (resetPwd.errorCode === 500) {
				message = t('pwdReset.errUser');
			} else {
				message = t('pwdReset.errGeneric');
			}

			setErrorMessage(message);
			setResetPwdError(true);
		}
	}, [resetPwd.error]);

	const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setResetPwdError(false);
		setErrorMessage('');
		resetPwd.resetError();

		if (pwd1 !== pwd2) {
			setResetPwdError(true);
			setErrorMessage(t('pwdReset.errMismatch'));
			return;
		}

		if (pwd1.trim().length === 0 || pwd1.length < 4 || pwd1.length > 100) {
			setResetPwdError(true);
			setErrorMessage(t('pwdReset.errLength'));
			return;
		}

		const resetPwdObject: KeyAndPasswordVM = {
			key: key || '',
			newPassword: pwd1
		};
		await resetPwd.fetch({ requestBody: resetPwdObject });
	};

	return (
		<div className={styles.main_container}>
			<form onSubmit={submitHandler} className={styles.form_container}>
				<Loader visible={resetPwd.loading} />

				<SuccessToast
					isVisible={resetPwdError}
					message={errorMessage}
					onClose={() => {
						setResetPwdError(false);
					}}
					type={'error'}
				/>

				<FadeInSection className={styles.form_box}>
					<div className={styles.form_header}>
						<Logo className={styles.logo} onClick={() => {}} />
					</div>

					<div className={styles.form_content}>
						<div className={styles.credentials_div}>
							<div className={styles.credential_couple}>
								<InputField
									type={'password'}
									title={t('pwdReset.newPwd')}
									required
									label={t('pwdReset.newPwd')}
									placeholder={t('pwdReset.newPwd')}
									ref={pwd1InputRef}
									onChange={(e) => {
										setPwd1(e.target.value);
									}}
									error={resetPwdError}
								/>
							</div>
							<div className={styles.credential_couple}>
								<InputField
									type={'password'}
									title={t('pwdReset.confirmPwd')}
									required
									label={t('pwdReset.confirmPwd')}
									placeholder={t('pwdReset.confirmPwd')}
									ref={pwd2InputRef}
									onChange={(e) => {
										setPwd2(e.target.value);
									}}
									error={resetPwdError}
								/>
							</div>
						</div>

						<div className={styles.box_btn}>
							<Button
								className={styles.btn_login}
								typeSubmit={'submit'}
								onClick={() => {}}
								name={t('pwdReset.finishReset')}
								disabled={resetPwd.loading}
								color="black"
								background="white"
							/>
						</div>

						<Link to="/login" className={styles.navLink}>
							<p className={styles.getBack_label}>{t('pwdReset.back')}</p>
						</Link>
					</div>
				</FadeInSection>
			</form>
		</div>
	);
};

export default PasswordResetFinish;
